import { joinPaths } from "./join-paths"

/**
 * Creates an absolute URL by combining a base URL with a relative path.
 *
 * @param path - The relative path to append to the base URL. Can be null, undefined, or empty.
 * @param options - Optional configuration for URL generation.
 * @returns The absolute URL string.
 *
 * @example
 * ```typescript
 * absoluteUrl("/api/users") // "https://example.com/api/users"
 * absoluteUrl("api/users")  // "https://example.com/api/users"
 * absoluteUrl("")           // "https://example.com"
 * absoluteUrl(null)         // "https://example.com"
 * absoluteUrl("/api/users", { query: { id: "123" } }) // "https://example.com/api/users?id=123"
 * absoluteUrl("/api/users", { fragment: "section1" }) // "https://example.com/api/users#section1"
 * ```
 */
export function absoluteUrl(
  path?: string | null,
  options?: {
    query?: Record<string, string | number | boolean | null | undefined>
    fragment?: string
    baseUrl?: string
  }
): string {
  const rawBase = options?.baseUrl ?? process.env.NEXT_PUBLIC_APP_URL ?? "http://localhost:3000"

  let url: URL
  try {
    url = new URL(rawBase)
  } catch {
    throw new Error(`Invalid base URL: ${rawBase}`)
  }

  if (path?.trim()) {
    url.pathname = joinPaths([url.pathname, path])
  }

  if (options?.query) {
    for (const [key, value] of Object.entries(options.query)) {
      if (value != null) {
        url.searchParams.append(key, String(value))
      }
    }
  }

  if (options?.fragment) {
    // Assigning to .hash handles encoding correctly; don't prepend '#'
    url.hash = options.fragment
  }

  return url.toString()
}
